import { IRuleAttachment } from "components/ContractLanguageSection/types";
import {
  RuleInheritedFrom,
  RulesetApplicability,
} from "types/DefaultRules/DefaultRules";
import { IRuleHistory } from "types/RuleHistory/RuleHistory";

export type InpatientRulesetType =
  | "FIXED_COST"
  | "PASS_THROUGH"
  | "EXCESSIVE_LOS"
  | "TRANSFER"
  | "STOP_LOSS"
  | "CHARGE_OUTLIER"
  | "INPATIENT_OUTLIER";

export interface IFixedCostConfig {
  costToChargeRatio: number;
  fixedLossThresholdInCents: number;
  outlierPercent: number;
}

export interface IPassThroughConfig {
  thresholdInCents: number;
  chargeMultiplier: number;
}

export interface IExcessiveLosConfig {
  formula: string;
}

export interface ITransferConfig {
  losFormula: "LOS" | "LOS_PLUS_1" | "LOS_MINUS_1";
  dischargeStatusCodes: string[];
}

export interface IStopLossConfig {
  thresholdMultiplier: number;
  paymentPercent: number;
}

export interface IChargeOutlierConfig {
  thresholdType: "FLAT" | "MULTIPLIER";
  thresholdAmountInCents?: number;
  thresholdMultiplier?: number;
  outlierPercent: number;
  chargeFloorInCents?: number;
}

// --- INPATIENT_OUTLIER (CLE-2120) ---

export type InpatientOutlierTriggerType =
  | "STANDARD_CHARGE_THRESHOLD"
  | "MODIFIED_CHARGE_THRESHOLD"
  | "LOS_RANGE"
  | "PER_CODE_CHARGE_THRESHOLD"
  | "DISCHARGE_STATUS"
  | "ALWAYS"
  | "ADJUSTMENT_CODE";

// CLE-2303 — closed set of DRG payment-breakdown fields available to the
// DRG_FIELD_SUM formula. Keep this list in sync with the backend
// `DRG_BREAKDOWN_FIELD_KEYS` constant in `inpatient_rulesets.types.ts`.
export const DRG_BREAKDOWN_FIELD_KEYS = [
  "capitalDsh",
  "capitalIme",
  "opsDsh",
  "opsIme",
  "opsDirectMedicalEducation",
  "opsUncompensatedCare",
  "opsReadmission",
  "opsValueBased",
  "opsNewTechnology",
  "otherPps",
] as const;
export type DrgBreakdownFieldKey = (typeof DRG_BREAKDOWN_FIELD_KEYS)[number];

// Human-readable labels for each DRG breakdown field — used in the rule form
// multi-select. Mirrors the comments on the Prisma columns added by CLE-2343.
export const DRG_BREAKDOWN_FIELD_LABELS: Record<DrgBreakdownFieldKey, string> =
  {
    capitalDsh: "Capital DSH",
    capitalIme: "Capital IME",
    opsDsh: "Operating DSH",
    opsIme: "Operating IME",
    opsDirectMedicalEducation: "Operating Direct Medical Education",
    opsUncompensatedCare: "Operating Uncompensated Care",
    opsReadmission: "Operating Readmission Adjustment",
    opsValueBased: "Operating Value-Based Purchasing",
    opsNewTechnology: "Operating New Technology",
    otherPps: "Other PPS",
  };

export type InpatientOutlierLosAdjustment = "NONE" | "PLUS_1" | "MINUS_1";

export type InpatientOutlierThresholdMethod =
  | "FIXED"
  | "MULTIPLIER"
  | "BASE_PLUS_FIXED";

// CLE-3080 — DRG_RATE_PLUS_LOS_RULES: the DRG rate plus this claim's additive LOS-range rules.
export type InpatientOutlierBaseReference =
  | "BASE_PAYMENT"
  | "DRG_RATE_ONLY"
  | "DRG_RATE_PLUS_LOS_RULES";

export type InpatientOutlierLosOperator = "GT" | "GTE" | "EQ" | "LTE" | "LT";

export type InpatientOutlierDaysSource = "MANUAL" | "FEE_SCHEDULE";

export type InpatientOutlierFormulaType =
  | "PER_DIEM_X_LOS"
  | "PER_DIEM_X_EXCESS_LOS"
  | "FLAT_RATE"
  | "PERCENT_OF_CHARGES"
  | "PERCENT_OF_EXCESS_CHARGES"
  | "CASE_RATE_PLUS_PER_DIEM"
  | "DRG_FIELD_SUM";

export type InpatientOutlierPerDiemSource =
  | "MANUAL"
  | "CASE_RATE_DIV_ALOS_TIMES_MULTIPLIER"
  | "CASE_RATE_DIV_ACTUAL_LOS"
  | "CASE_RATE_DIV_ALOS";

export type InpatientOutlierMode = "REPLACE" | "ADDITIVE" | "MAX";

export interface IStandardChargeThresholdTrigger {
  type: "STANDARD_CHARGE_THRESHOLD";
  thresholdMethod: InpatientOutlierThresholdMethod;
  baseReference?: InpatientOutlierBaseReference;
  fixedAmountInCents?: number;
  multiplier?: number;
  chargeFloorInCents?: number;
}

export interface IModifiedChargeThresholdTrigger {
  type: "MODIFIED_CHARGE_THRESHOLD";
  costToChargeRatio: number;
  thresholdMethod: InpatientOutlierThresholdMethod;
  baseReference?: InpatientOutlierBaseReference;
  fixedAmountInCents?: number;
  multiplier?: number;
  chargeFloorInCents?: number;
}

export interface ILosRangeTrigger {
  type: "LOS_RANGE";
  operator: InpatientOutlierLosOperator;
  days: number | null;
  daysSource: InpatientOutlierDaysSource;
}

export interface IPerCodeChargeThresholdTrigger {
  type: "PER_CODE_CHARGE_THRESHOLD";
  codeChargeFloorInCents: number;
  onlyCodesWithoutFeeSchedule?: boolean;
}

export interface IDischargeStatusTrigger {
  type: "DISCHARGE_STATUS";
  codes: string[];
}

export interface IAlwaysTrigger {
  type: "ALWAYS";
}

// CLE-2303 — fires when the claim's adjustments include any of the configured
// CARC codes (full GROUP-CODE form, e.g. 'CO-74'). Optional
// `requireDominancePercent` adds a guard: matched-code amounts must reach
// that percentage of total charges before the trigger fires.
export interface IAdjustmentCodeTrigger {
  type: "ADJUSTMENT_CODE";
  codes: string[];
  requireDominancePercent?: number | null;
}

export type InpatientOutlierTrigger =
  | IStandardChargeThresholdTrigger
  | IModifiedChargeThresholdTrigger
  | ILosRangeTrigger
  | IPerCodeChargeThresholdTrigger
  | IDischargeStatusTrigger
  | IAlwaysTrigger
  | IAdjustmentCodeTrigger;

export interface IPerDiemTimesLosFormula {
  type: "PER_DIEM_X_LOS";
  perDiemSource: InpatientOutlierPerDiemSource;
  manualPerDiemInCents?: number;
  losAdjustment?: InpatientOutlierLosAdjustment;
  capAtDrgRate?: boolean;
}

export interface IPerDiemTimesExcessLosFormula {
  type: "PER_DIEM_X_EXCESS_LOS";
  perDiemSource: InpatientOutlierPerDiemSource;
  manualPerDiemInCents?: number;
  excessLosBase: number | null;
  excessLosBaseSource: InpatientOutlierDaysSource;
}

export interface IFlatRateFormula {
  type: "FLAT_RATE";
  flatRateInCents: number;
}

export interface IPercentOfChargesFormula {
  type: "PERCENT_OF_CHARGES";
  percent: number;
}

export interface IPercentOfExcessChargesFormula {
  type: "PERCENT_OF_EXCESS_CHARGES";
  percent: number;
}

export interface ICaseRatePlusPerDiemFormula {
  type: "CASE_RATE_PLUS_PER_DIEM";
  caseRateInCents: number;
  includedDays: number;
  perDiemInCents: number;
}

// CLE-2303 — sums a configurable subset of the 10 DRG payment-breakdown fields
// on the matching ProviderProcedure row. Null-strict: missing rows or any null
// selected field cause the rule to no-op.
export interface IDrgFieldSumFormula {
  type: "DRG_FIELD_SUM";
  fields: DrgBreakdownFieldKey[];
}

export type InpatientOutlierFormula =
  | IPerDiemTimesLosFormula
  | IPerDiemTimesExcessLosFormula
  | IFlatRateFormula
  | IPercentOfChargesFormula
  | IPercentOfExcessChargesFormula
  | ICaseRatePlusPerDiemFormula
  | IDrgFieldSumFormula;

export interface IInpatientOutlierConfig {
  trigger: InpatientOutlierTrigger;
  formula: InpatientOutlierFormula;
  mode: InpatientOutlierMode;
}

export type InpatientRulesetConfig =
  | IFixedCostConfig
  | IPassThroughConfig
  | IExcessiveLosConfig
  | ITransferConfig
  | IStopLossConfig
  | IChargeOutlierConfig
  | IInpatientOutlierConfig;

export interface IInpatientRuleset {
  id: string;
  providerId: string | null;
  payerId: number | null;
  name: string;
  drgCodes: string[];
  type: InpatientRulesetType;
  config: InpatientRulesetConfig;
  provider?: { id: string; name: string };
  payer?: { id: number; name: string };
  createdAt: string;
  updatedAt: string;
  // CLE-2524 — TYPICAL (inert template) | SCOPED; FALLBACK since CLE-2842 step 2.
  applicability: RulesetApplicability;
  isDefault: boolean;
  sourceDefaultId: string | null;
  modifiedFields: string[];
  contractLanguage?: string | null;
  attachments?: IRuleAttachment[];
  history?: IRuleHistory[];
  /** CLE-2842 step 2 — contract detail only: set when the rule reaches the contract from a less specific rung. */
  inheritedFrom?: RuleInheritedFrom;
}

export interface IInpatientRulesetResponse {
  data: IInpatientRuleset;
  warning?: string | null;
  message: string;
}

export interface IAllInpatientRulesetsResponse {
  data: IInpatientRuleset[];
  message: string;
}
